/**
 * MHCard - Mindhunters Governance-Compliant Card Component
 *
 * Layer 3: MH Components (Governance Layer)
 * - Wraps shadcn/ui Card primitive
 * - Enforces brand styling
 *
 * Card types (from FRONTEND_GOVERNANCE):
 * - info: General information cards
 * - decision: Decision-related cards (used in Room)
 */

'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { MHStack } from '../layout/MHStack';

const mhCardVariants = cva(
  'rounded-2xl transition-colors duration-hover ease-calm',
  {
    variants: {
      type: {
        // Info: General information display
        info: 'bg-surface-1 text-text-primary',

        // Decision: Decision context cards
        decision: 'bg-surface-2 text-text-primary',
      },
    },
    defaultVariants: {
      type: 'info',
    },
  }
);

export interface MHCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mhCardVariants> {
  interactive?: boolean;
}

const MHCard = React.forwardRef<HTMLDivElement, MHCardProps>(
  ({ className, type, interactive = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          mhCardVariants({ type, className }),
          interactive && 'mh-interactive'
        )}
        {...props}
      />
    );
  }
);

MHCard.displayName = 'MHCard';

// Card sub-components with governance styling
const MHCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <MHStack
    ref={ref}
    gap={8}
    className={cn('p-6', className)}
    {...props}
  />
));
MHCardHeader.displayName = 'MHCardHeader';

const MHCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-h3 font-semibold text-text-primary', className)}
    {...props}
  />
));
MHCardTitle.displayName = 'MHCardTitle';

const MHCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-small text-text-secondary', className)}
    {...props}
  />
));
MHCardDescription.displayName = 'MHCardDescription';

const MHCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
MHCardContent.displayName = 'MHCardContent';

const MHCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <MHStack
    ref={ref}
    direction="horizontal"
    gap={12}
    align="center"
    className={cn('p-6 pt-0', className)}
    {...props}
  />
));
MHCardFooter.displayName = 'MHCardFooter';

export {
  MHCard,
  MHCardHeader,
  MHCardFooter,
  MHCardTitle,
  MHCardDescription,
  MHCardContent,
  mhCardVariants,
};
